import Transfer from "@/model/transfer.mjs";
import {TransferForm} from "./TransferForm";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { TransferDetailsType, TransferFormMode, TransferMacrostatus, TransferModalProps } from "../types";
import { getTotalTransfersCount } from "../ducks/selectors";
import { setCreateTransferData, setLicenseeDetail, setSequalizeValidationError, setTotalTransfersCount } from "../ducks/slice";
import { createTransfer, editTransfer, getTranferDetails } from "@/api/apis";
import { OutlineToasterNotification } from "@/toaster/successNotification";
import { AxiosError } from "axios";
import ModalComponent from "@/common/Modal";
import { Button } from "@chakra-ui/button";
import { TransferDetailsInitialState } from "@/utils/constants";
import { getIsGlobalAdmin } from "@/redux/app-selectors";

interface EditTransferModalInterface extends TransferModalProps {
    transferToBeEdit?: {
      id: string;
  } | null;
}

export default function EditTransferModal({
    isOpen,
    onClose,
    licensee,
    refetchData,
    transferToBeEdit
  }: EditTransferModalInterface) {
    const [isSavingDraftTransfer, setIsSavingDraftTransfer] = useState<boolean>(false);
    const [isInitiatingTransfer, setIsInitiatingTransfer] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(false);
    const [isFetchingDetails, setIsFetchingDetails] = useState(false);
    const [transferDetails, setTransferDetails] =
    useState<TransferDetailsType>(TransferDetailsInitialState);
    const dispatch = useDispatch();
    const isSuperAdmin = getIsGlobalAdmin()
    const [editTransferValues, setEditTransferValues] = useState<Transfer>({
      key: '',
      referenceId: '',
      firearm: '',
      status: '',
      buyerName: '',
      agent: '',
      notes: [''],
      orderDate: null,
      sku: '',
      storeURL: '',
      buyerPhone: '',
      buyerEmail: '',
      modelType: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      age: ''
    });

    function callEditTransferApi(isInitiating: boolean) {
      return async () => {
        if(isInitiating)
          setIsInitiatingTransfer(true);
        else
          setIsSavingDraftTransfer(true);
        try {
          const {
            street,
            city,
            state,
            zipCode,
            buyerPhone,
            buyerEmail,
            buyerName,
            key,
            referenceId,
            ...restCrateTransferPayload
          } = editTransferValues;
          const createTransferPayload = {
            licenseKey: key,
            orderId: referenceId,
            buyer: {
              street: street || "",
              city: city || "",
              state: state || "",
              zipCode: zipCode || "",
              phone: buyerPhone || "",
              email: buyerEmail || "",
              name: buyerName || ""
            },
            isInitiated: isInitiating,
            ...restCrateTransferPayload
          };
          await editTransfer(transferToBeEdit?.id as string, createTransferPayload);
          onClose();
          if (refetchData) {
            refetchData();
          }
        } catch (err) {
          const error = err as AxiosError<{ code: number }>;
          if (error?.response?.data?.code === 71)
            dispatch(
              setSequalizeValidationError(
                'Order ID already exists. Enter an unique ID'
              )
            );
          console.log({ afds: error });
        } finally {
          if(isInitiating)
            setIsInitiatingTransfer(false);
          else
          setIsSavingDraftTransfer(false);
          setDisabled(false);
          dispatch(setLicenseeDetail(null));
        }
      }
    }

    useEffect(() => {
      fetchTransferDetails();
    }, [])

    const fetchTransferDetails = async () => {
      setIsFetchingDetails(true);
      try {
        const response = (await getTranferDetails({ id: transferToBeEdit?.id || '' })) || {};
        setTransferDetails(response.data);
        setIsFetchingDetails(false);
      } catch (error) {
        setIsFetchingDetails(false);
      }
    };
    
    return (
      <ModalComponent
        title="Edit Transfer"
        isOpen={isOpen}
        onClose={()=>{
          onClose();
          dispatch(setLicenseeDetail(null));
        }}
        variant={2}
        modalType="createTransfer"
        footer={
          <>
            <Button
              colorScheme="blue"
              background="#ffffff"
              color="#0C1E46"
              border="1px solid #0C1E46"
              padding="8px 11px"
              marginRight="20px"
              isDisabled={isSavingDraftTransfer || isInitiatingTransfer}
              onClick={() => {
                onClose();
                setDisabled(false);
                dispatch(setLicenseeDetail(null));
              }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              background="#0C1E46"
              padding="8px 20px"
              marginRight="20px"
              onClick={callEditTransferApi(false)}
              isLoading={isSavingDraftTransfer}
              isDisabled={!disabled || isSavingDraftTransfer || isInitiatingTransfer || isSuperAdmin}
            >
              Save
            </Button>
            <Button
              colorScheme="blue"
              background="#0C1E46"
              padding="8px 20px"
              onClick={callEditTransferApi(true)}
              isLoading={isInitiatingTransfer}
              isDisabled={!disabled || isSavingDraftTransfer || isInitiatingTransfer || isSuperAdmin}
            >
              Initiate Transfer
            </Button>
          </>
        }
      >
        <TransferForm
          licensee={licensee}
          createTransfer={setEditTransferValues}
          canCreatetransfer={setDisabled}
          isSubmitting={isSavingDraftTransfer || isInitiatingTransfer}
          formInitialData={transferDetails}
          mode={TransferFormMode.TRANSFER_EDIT}
        />
      </ModalComponent>
    );
  }
  